import { useEffect, useState } from "react";
import { Check, Info, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { ButtonTip } from "@/components/ui/button-tip";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  OnboardingAdminError,
  onboardingAdminAPI,
  onboardingLinkFor,
  type AdminLeadSummary,
  type InviteWarning,
} from "../../OnboardingAPI";
import CopyLinkBox from "./CopyLinkBox";
import { errorText } from "./cockpitErrors";

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const DEFAULT_DAYS = 30;
/** The server accepts 1 to 365 days and answers 400 otherwise. */
export const MAX_DAYS = 365;

/** The pause after leaving the e-mail field before the address is checked. */
export const CHECK_DELAY_MS = 400;

/** The sentences for the warnings of a created invitation, by their code (the server text only for a code this page does not know). */
const warningTexts = (warnings: InviteWarning[]): string[] =>
  warnings
    .map((warning) => (warning.code === "existing_user" ? EXISTING_USER_NOTICE : warning.code === "open_lead" ? openLeadsNotice(1) : warning.message))
    .filter((text, index, all) => all.indexOf(text) === index);

export const LINK_ONCE_NOTICE =
  "Der Link wird nur jetzt angezeigt; später über „Neuen Link ausstellen“ in der Detailansicht.";

/** The link of a new or re-issued token, shown once. */
export function LinkShownOnce({ token }: { token: string }) {
  return (
    <div className="space-y-3">
      <CopyLinkBox link={onboardingLinkFor(token)} />
      <p role="note" className="text-sm text-muted-foreground">
        {LINK_ONCE_NOTICE}
      </p>
    </div>
  );
}

/** The sentences about an address that already has an account or invitations (one place for the form and for the confirmation). */
export const EXISTING_USER_NOTICE =
  "Für diese E-Mail-Adresse gibt es schon einen Zugang. Beim Absenden wird deshalb kein Mandant angelegt und keine Willkommensmail verschickt.";
/** At the field: an address with an access cannot be invited again. (After the creation the server's warning says what it does at the release.) */
export const EXISTING_USER_BLOCKED_NOTICE = "Für diese E-Mail-Adresse gibt es schon einen Zugang. Mit ihr kann keine neue Einladung erstellt werden.";
/** The hint under the e-mail field: any address is accepted, but it should be the person's company address. */
export const COMPANY_MAIL_HINT = "Bitte verwende die Firmen-E-Mail-Adresse der Person.";
export const openLeadsNotice = (count: number) =>
  count === 1 ? "Für diese Adresse gibt es schon eine offene Einladung." : `Für diese Adresse gibt es schon ${count} offene Einladungen.`;

/** Notices as their own white cards (icon and text), never written into the form card; they do not block anything. */
export function ContactNotices({ notices }: { notices: string[] }) {
  if (notices.length === 0) return null;
  return (
    <div className="space-y-3" data-testid="contact-notices">
      {notices.map((text) => (
        <div key={text} role="note" className="flex items-start gap-3 rounded-2xl border border-border bg-white p-3 text-sm text-foreground shadow-sm dark:bg-card">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          <p>{text}</p>
        </div>
      ))}
    </div>
  );
}

/** The fields of the form, by the names the server uses. */
const FIELDS = ["companyName", "contactEmail", "expiresInDays"];

/**
 * "Neue Einladung": the form, then - once the server has issued the token - the link, which exists
 * only in this response and never again.
 */
export default function InviteDialog({
  open,
  onOpenChange,
  onCreated,
  onError,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (lead: AdminLeadSummary) => void;
  onError: (error: unknown) => void;
}) {
  const [companyName, setCompanyName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [days, setDays] = useState(String(DEFAULT_DAYS));
  const [busy, setBusy] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);
  // a field the server refused (400 invalid_request with `field`), with its own sentence; it stays until anything is edited
  const [warnings, setWarnings] = useState<InviteWarning[]>([]);
  // The live check of the address: typed, then 400 ms of quiet, then one request for the address as it stands (an answer
  // for an older one is dropped). `taken` blocks the button, an open invitation only warns, a failed check is silent.
  type Check = { email: string; state: "checking" | "taken" | "free" | "unknown"; openLeads: number };
  const [check, setCheck] = useState<Check | null>(null);
  const [refused, setRefused] = useState<{ field: string; message: string; snapshot: string } | null>(null);
  const snapshot = JSON.stringify([companyName, contactEmail, days]);
  const fieldNote = (name: string) =>
    refused && refused.field === name && refused.snapshot === snapshot ? (
      <p role="alert" data-testid={`server-error-${name}`} className="text-xs text-destructive">
        {refused.message}
      </p>
    ) : null;

  const daysNumber = Number(days);
  const daysValid = Number.isInteger(daysNumber) && daysNumber >= 1 && daysNumber <= MAX_DAYS;
  const emailValid = EMAIL.test(contactEmail.trim());
  // the address as the server compares it
  const normalized = contactEmail.trim().toLowerCase();
  const current = check && check.email === normalized ? check : null;
  const checking = emailValid && (!current || current.state === "checking");
  const taken = current?.state === "taken";
  // why "Einladung erstellen" is off: one list of reasons, and the button is on exactly when none applies
  const createBlocker = !companyName.trim()
    ? "Trage zuerst die Firma ein."
    : !emailValid
      ? "Trage eine gültige E-Mail-Adresse ein."
      : !daysValid
        ? `Die Gültigkeit muss zwischen 1 und ${MAX_DAYS} Tagen liegen.`
        : checking
          ? "Die Adresse wird noch geprüft."
          : taken
            ? "Für diese Adresse gibt es schon einen Zugang."
            : busy
              ? "Die Einladung wird gerade erstellt."
              : null;
  const canCreate = createBlocker === null;

  useEffect(() => {
    if (!EMAIL.test(normalized)) {
      setCheck(null);
      return;
    }
    let dropped = false;
    setCheck({ email: normalized, state: "checking", openLeads: 0 });
    const timer = window.setTimeout(() => {
      Promise.resolve()
        .then(() => onboardingAdminAPI.checkContact(normalized))
        .then((result) => {
          if (dropped) return;
          const openLeads = Number(result?.openLeads) || 0;
          // a private address (gmail and the like) is no reason to refuse: only an address that has an access is
          setCheck({ email: normalized, state: result?.existingUser ? "taken" : "free", openLeads });
        })
        // a network failure or a server without the route: no message, the button stays usable (the server checks again at the release)
        .catch(() => {
          if (!dropped) setCheck({ email: normalized, state: "unknown", openLeads: 0 });
        });
    }, CHECK_DELAY_MS);
    return () => {
      dropped = true;
      window.clearTimeout(timer);
    };
  }, [normalized]);

  const noticesOf = (): string[] => {
    const list: string[] = [];
    if (taken) list.push(EXISTING_USER_BLOCKED_NOTICE);
    if (current && (current.state === "taken" || current.state === "free") && current.openLeads > 0) list.push(openLeadsNotice(current.openLeads));
    return list;
  };

  const reset = () => {
    setCompanyName("");
    setContactEmail("");
    setDays(String(DEFAULT_DAYS));
    setToken(null);
    setTouched(false);
    setRefused(null);
    setCheck(null);
    setWarnings([]);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const create = async () => {
    setTouched(true);
    if (!canCreate) return;
    setBusy(true);
    try {
      // nothing else is sent: the sender is whoever is signed in (the server sets it), the contact name is the customer's to enter
      const result = await onboardingAdminAPI.create({ companyName: companyName.trim(), contactEmail: contactEmail.trim(), expiresInDays: daysNumber });
      setToken(result.token);
      setWarnings(result.warnings ?? []);
      onCreated(result.lead);
    } catch (error) {
      if (error instanceof OnboardingAdminError && error.code === "invalid_request" && error.field && FIELDS.includes(error.field)) {
        // shown at the field it is about; no error dialog
        setRefused({ field: error.field, message: errorText(error, "Die Eingabe wurde nicht angenommen."), snapshot });
      } else {
        onError(error);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
        {token ? (
          <>
            <DialogHeader>
              <DialogTitle>Einladung erstellt</DialogTitle>
              <DialogDescription>Diesen Link schickst du dem Kunden. Eine Mail verschickt das Portal dazu nicht.</DialogDescription>
            </DialogHeader>
            <ContactNotices notices={warningTexts(warnings)} />
            <LinkShownOnce token={token} />
            <DialogFooter>
              <ButtonTip label="Schließt den Dialog">
                <Button onClick={() => handleOpenChange(false)}>Fertig</Button>
              </ButtonTip>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Neue Einladung</DialogTitle>
              <DialogDescription>
                Legt einen Lead mit einem persönlichen Link an. Der Kunde trägt dort seinen Namen und sein Profil ein und lädt seine Daten hoch. Absender ist, wer den Link erstellt.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="lead-company">Firma</Label>
                <Input id="lead-company" value={companyName} onChange={(event) => setCompanyName(event.target.value)} onBlur={() => setTouched(true)} />
                {touched && !companyName.trim() && <p className="text-xs text-destructive">Bitte die Firma angeben.</p>}
                {fieldNote("companyName")}
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="lead-email">Firmen-E-Mail-Adresse</Label>
                  <Input
                    id="lead-email"
                    type="email"
                    value={contactEmail}
                    onChange={(event) => setContactEmail(event.target.value)}
                    onBlur={() => setTouched(true)}
                    aria-invalid={taken ? true : undefined}
                    placeholder="name@firma.de"
                    aria-describedby="lead-email-hint lead-email-check"
                  />
                  <p id="lead-email-hint" className="text-xs text-muted-foreground" data-testid="email-hint">
                    {COMPANY_MAIL_HINT}
                  </p>
                  {touched && !EMAIL.test(contactEmail.trim()) && (
                    <p className="text-xs text-destructive">Bitte eine gültige E-Mail-Adresse angeben.</p>
                  )}
                  {fieldNote("contactEmail")}
                  <div id="lead-email-check" className="min-h-4 text-xs text-muted-foreground" aria-live="polite" data-testid="email-check">
                    {checking && (
                      <span className="flex items-center gap-1.5">
                        <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
                        Prüfe Adresse …
                      </span>
                    )}
                    {!checking && current?.state === "free" && current.openLeads === 0 && (
                      <span className="flex items-center gap-1.5" data-testid="email-free">
                        <Check className="h-3 w-3" aria-hidden="true" />
                        Adresse ist frei
                      </span>
                    )}
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="lead-days">Gültigkeit in Tagen</Label>
                  <Input id="lead-days" type="number" min={1} max={MAX_DAYS} value={days} onChange={(event) => setDays(event.target.value)} />
                  {!daysValid && <p className="text-xs text-destructive">Bitte eine ganze Zahl von 1 bis 365 angeben.</p>}
                  {fieldNote("expiresInDays")}
                </div>
              </div>
            </div>
            <ContactNotices notices={noticesOf()} />
            <DialogFooter>
              <ButtonTip label="Schließt den Dialog, ohne etwas anzulegen">
                <Button variant="outline" onClick={() => handleOpenChange(false)}>
                  Abbrechen
                </Button>
              </ButtonTip>
              <ButtonTip label={canCreate ? "Legt den Lead an und zeigt den persönlichen Link einmalig an" : createBlocker} disabled={!canCreate}>
                <Button onClick={() => void create()} disabled={!canCreate}>
                  {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
                  Einladung erstellen
                </Button>
              </ButtonTip>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
